import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Save, RotateCcw, Plus, Trash2, FlaskConical, History, Bot } from "lucide-react";

const MODEL_LABELS = {
  "gpt-5.6-luna": "GPT-5.6 Luna",
  "gpt-5.6-terra": "GPT-5.6 Terra",
  "gpt-5.6-sol": "GPT-5.6 Sol",
};

const TOGGLES = [
  ["master", "WhatsApp AI Master"], ["auto_reply", "AI Auto Reply"],
  ["live_appointment_lookup", "Live Appointment Lookup"], ["appointment_booking", "Appointment Booking"],
  ["appointment_cancellation", "Appointment Cancellation"], ["appointment_rescheduling", "Appointment Rescheduling"],
  ["live_staff_availability", "Live Staff Availability"], ["service_price_info", "Service & Price Info"],
  ["offers_packages_info", "Offers & Packages Info"], ["freedom_package", "Freedom Package Replies"],
  ["branch_information", "Branch Information"], ["customer_history", "Customer Booking History"],
  ["human_handover", "Human Handover"],
];

const BIZ = [
  ["salon", "Salon Information", [["description", "Salon description"], ["contact", "Contact information"], ["location", "Location information"]]],
  ["booking_rules", "Booking Rules", [["booking", "Booking instructions"], ["cancellation", "Cancellation rules"], ["rescheduling", "Rescheduling rules"], ["late_arrival", "Late-arrival rules"]]],
  ["sales", "AI Sales Instructions", [["promote_services", "Services to promote"], ["promote_packages", "Packages to promote"], ["current_offers", "Current offers"], ["upselling", "Upselling instructions"], ["booking_link", "Booking link"]]],
];

export function AIControlPanel() {
  const [cfg, setCfg] = useState(null);
  const [instr, setInstr] = useState("");
  const [biz, setBiz] = useState({});
  const [newRule, setNewRule] = useState("");
  const [test, setTest] = useState("");
  const [testRes, setTestRes] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testNum, setTestNum] = useState(() => "9199" + Math.floor(100000 + Math.random() * 900000));
  const [thread, setThread] = useState([]);

  const load = () => api.get("/wa-ai-config").then((r) => {
    setCfg(r.data); setInstr(r.data.instructions || ""); setBiz(r.data.business || {});
  }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const saveToggles = async (patch) => {
    try { const { data } = await api.put("/wa-ai-config", { toggles: patch }); setCfg(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveInstr = async () => {
    try { const { data } = await api.put("/wa-ai-config", { instructions: instr, instructions_enabled: cfg.instructions_enabled }); setCfg(data); toast.success("Instructions saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resetInstr = async () => {
    try { const { data } = await api.post("/wa-ai-config/reset-instructions"); setCfg(data); setInstr(data.instructions); toast.success("Reset to default"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveBiz = async () => {
    try { const { data } = await api.put("/wa-ai-config", { business: biz }); setCfg(data); toast.success("Business info saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveModel = async (m) => {
    try { const { data } = await api.put("/wa-ai-config", { ai_model: m }); setCfg(data); toast.success(`AI model set to ${MODEL_LABELS[m] || m}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addRule = async () => {
    if (!newRule.trim()) return;
    try { const { data } = await api.post("/wa-ai-config/rules", { text: newRule.trim(), enabled: true }); setCfg({ ...cfg, rules: data.rules }); setNewRule(""); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleRule = async (r) => {
    try { const { data } = await api.put(`/wa-ai-config/rules/${r.id}`, { text: r.text, enabled: !r.enabled }); setCfg({ ...cfg, rules: data.rules }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delRule = async (r) => {
    try { const { data } = await api.delete(`/wa-ai-config/rules/${r.id}`); setCfg({ ...cfg, rules: data.rules }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const runTest = async () => {
    if (!test.trim()) return;
    const q = test.trim();
    setTesting(true); setTestRes(null);
    setThread((t) => [...t, { role: "user", text: q }]);
    setTest("");
    try {
      const { data } = await api.post("/wa-ai-config/test", { text: q, from_number: testNum });
      setTestRes(data);
      setThread((t) => [...t, { role: "assistant", text: data.reply || "(no reply)" }]);
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };
  const newTestChat = () => {
    setTestNum("9199" + Math.floor(100000 + Math.random() * 900000));
    setThread([]); setTestRes(null); setTest("");
    toast.success("Started a new test conversation");
  };

  if (!cfg) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const setBizField = (sec, field, val) => setBiz((b) => ({ ...b, [sec]: { ...(b[sec] || {}), [field]: val } }));

  return (
    <div className="space-y-4" data-testid="ai-control-panel">
      {/* AI Model selection */}
      <Card className="p-4 bg-card border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-display text-lg flex items-center gap-2"><Bot size={18} /> AI Model</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">Choose the model powering WhatsApp AI replies. Applies live — no redeploy needed.</p>
          </div>
          <Select value={cfg.ai_model || "gpt-5.6-luna"} onValueChange={saveModel}>
            <SelectTrigger className="w-full sm:w-56 bg-secondary" data-testid="ai-model-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(cfg.available_models || ["gpt-5.6-luna", "gpt-5.6-terra", "gpt-5.6-sol"]).map((m) => (
                <SelectItem key={m} value={m} data-testid={`ai-model-option-${m}`}>{MODEL_LABELS[m] || m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Master controls */}
      <Card className="p-4 bg-card border-border">
        <h3 className="font-display text-lg mb-3">AI Master Controls</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
          {TOGGLES.map(([k, label]) => (
            <label key={k} className="flex items-center justify-between gap-2 py-1 cursor-pointer">
              <span className={`text-sm ${k === "master" ? "font-semibold" : ""}`}>{label}</span>
              <Switch checked={!!cfg.toggles[k]} onCheckedChange={(v) => saveToggles({ [k]: v })} data-testid={`ai-toggle-${k}`} />
            </label>
          ))}
        </div>
      </Card>

      {/* Custom instructions */}
      <Card className="p-4 bg-card border-border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-lg">Custom AI Instructions</h3>
          <label className="flex items-center gap-2 text-xs cursor-pointer"><Switch checked={!!cfg.instructions_enabled} onCheckedChange={(v) => setCfg({ ...cfg, instructions_enabled: v })} data-testid="ai-instr-enabled" /> Use custom instructions</label>
        </div>
        <Textarea rows={6} value={instr} onChange={(e) => setInstr(e.target.value)} className="bg-secondary text-sm" data-testid="ai-instructions" placeholder="Type instructions the AI must follow…" />
        <div className="flex gap-2 mt-2">
          <Button onClick={saveInstr} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="ai-save-instructions"><Save size={14} className="mr-1" /> Save Instructions</Button>
          <Button variant="outline" onClick={resetInstr} className="rounded-sm" data-testid="ai-reset-instructions"><RotateCcw size={14} className="mr-1" /> Reset to Default</Button>
        </div>
      </Card>

      {/* Quick rules */}
      <Card className="p-4 bg-card border-border">
        <h3 className="font-display text-lg mb-2">AI Rules</h3>
        <div className="flex gap-2 mb-3">
          <Input value={newRule} onChange={(e) => setNewRule(e.target.value)} placeholder="Add a rule…" className="bg-secondary" data-testid="ai-rule-input" />
          <Button onClick={addRule} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="ai-rule-add"><Plus size={15} /></Button>
        </div>
        <div className="space-y-2">
          {(cfg.rules || []).map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 rounded-sm border border-border px-3 py-2" data-testid={`ai-rule-${r.id}`}>
              <span className="text-sm flex-1">{r.text}</span>
              <Badge variant="outline" className={r.enabled ? "border-green-500 text-green-400" : "border-muted text-muted-foreground"}>{r.enabled ? "ON" : "OFF"}</Badge>
              <Switch checked={r.enabled} onCheckedChange={() => toggleRule(r)} data-testid={`ai-rule-toggle-${r.id}`} />
              <button onClick={() => delRule(r)} className="text-destructive" data-testid={`ai-rule-del-${r.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </Card>

      {/* Business info */}
      <Card className="p-4 bg-card border-border">
        <h3 className="font-display text-lg mb-3">Business Information</h3>
        <div className="space-y-4">
          {BIZ.map(([sec, title, fields]) => (
            <div key={sec}>
              <p className="text-sm font-medium text-primary mb-1">{title}</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {fields.map(([f, label]) => (
                  <div key={f}><Label className="text-[11px]">{label}</Label>
                    <Input value={(biz[sec] || {})[f] || ""} onChange={(e) => setBizField(sec, f, e.target.value)} className="bg-secondary mt-0.5 h-9" data-testid={`ai-biz-${sec}-${f}`} /></div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <Button onClick={saveBiz} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37] mt-3" data-testid="ai-save-biz"><Save size={14} className="mr-1" /> Save Business Info</Button>
      </Card>

      {/* Test AI */}
      <Card className="p-4 bg-card border-border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-lg flex items-center gap-2"><FlaskConical size={18} /> Test AI</h3>
          <Button variant="outline" size="sm" onClick={newTestChat} className="rounded-sm" data-testid="ai-test-new-chat"><RotateCcw size={13} className="mr-1" /> New conversation</Button>
        </div>
        <p className="text-[11px] text-muted-foreground mb-2">Runs against live data — does NOT send a real WhatsApp message. This test chat <b>remembers your previous messages</b> (session {testNum}), so a follow-up like "yes, confirm" continues the same booking.</p>
        {thread.length > 0 && (
          <div className="mb-3 space-y-2 max-h-72 overflow-y-auto rounded-sm border border-border p-3" data-testid="ai-test-thread">
            {thread.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary/20 text-foreground" : "bg-secondary text-foreground"}`} data-testid={`ai-test-msg-${m.role}-${i}`}>{m.text}</div>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Input value={test} onChange={(e) => setTest(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") runTest(); }} placeholder="Type a customer question…" className="bg-secondary" data-testid="ai-test-input" />
          <Button onClick={runTest} disabled={testing} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="ai-test-run">{testing ? "…" : "Send"}</Button>
        </div>
        {testRes && (
          <div className="mt-3 rounded-sm border border-border p-3 space-y-2 text-sm" data-testid="ai-test-result">
            {testRes.disabled ? <p className="text-amber-400">{testRes.message}</p> : <>
              <div><span className="text-muted-foreground text-xs">AI reply:</span><p className="whitespace-pre-wrap mt-0.5">{testRes.reply || "(no reply)"}</p></div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <span>Live DB checked: <b className={testRes.live_db_checked ? "text-green-400" : "text-muted-foreground"}>{testRes.live_db_checked ? "Yes" : "No"}</b></span>
                <span>Appointment lookup: <b className={testRes.appointment_lookup_checked ? "text-green-400" : "text-muted-foreground"}>{testRes.appointment_lookup_checked ? "Yes" : "No"}</b></span>
                <span>Staff availability checked: <b className={testRes.staff_availability_checked ? "text-green-400" : "text-muted-foreground"}>{testRes.staff_availability_checked ? "Yes" : "No"}</b></span>
                <span>Instructions applied: <b className={testRes.instructions_applied ? "text-green-400" : "text-muted-foreground"}>{testRes.instructions_applied ? "Yes" : "No"}</b></span>
              </div>
              <p className="text-xs text-muted-foreground">Data source / tools: {(testRes.tools_used || []).join(", ") || "general reply"}</p>
              {testRes.appointment_result && <p className="text-[11px] text-muted-foreground">Appointment lookup returned {(testRes.appointment_result.upcoming || []).length} upcoming, {(testRes.appointment_result.recent || []).length} recent</p>}
            </>}
          </div>
        )}
      </Card>

      {/* Audit history */}
      <Card className="p-4 bg-card border-border">
        <h3 className="font-display text-lg mb-2 flex items-center gap-2"><History size={18} /> Instruction Change History</h3>
        {(!cfg.audit || cfg.audit.length === 0) ? <p className="text-sm text-muted-foreground">No changes yet.</p> : (
          <div className="space-y-2" data-testid="ai-audit">
            {cfg.audit.map((a, i) => (
              <div key={i} className="text-xs border-b border-border/50 pb-2">
                <p className="text-muted-foreground">{a.at} · by {a.by || "—"}</p>
                <p className="text-red-300/80 line-clamp-2">− {a.prev || "(empty)"}</p>
                <p className="text-green-300/80 line-clamp-2">+ {a.new || "(empty)"}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
